import axios from "axios";
import { authService } from "./auth.service"; // adjust path/name if different

// Prefer from config, else fallback
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5172/api";

const getAuthHeaders = async () => {
  const token = await authService.getToken(); // make sure this exists
  return { Authorization: `Bearer ${token}` };
};

export const adminService = {
  // ===== Analytics =====
  async getOverview(params = {}) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/analytics/overview`, {
      headers,
      params,
    });
    return res.data;
  },

  async getRevenueByDay(params = {}) {
    const headers = await getAuthHeaders();
    const res = await axios.get(
      `${API_BASE_URL}/admin/analytics/revenue-by-day`,
      { headers, params },
    );
    return res.data;
  },

  async getTopProducts(params = {}) {
    const headers = await getAuthHeaders();
    const res = await axios.get(
      `${API_BASE_URL}/admin/analytics/top-products`,
      { headers, params },
    );
    return res.data;
  },

  async getTopCustomers(params = {}) {
    const headers = await getAuthHeaders();
    const res = await axios.get(
      `${API_BASE_URL}/admin/analytics/top-customers`,
      { headers, params },
    );
    return res.data;
  },

  // ===== Products =====
  async getProducts() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/products?limit=100`, {
      headers,
    });
    return res.data;
  },

  async getProduct(id) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/products/${id}`, {
      headers,
    });
    return res.data;
  },

  async createProduct(payload) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/products`, payload, {
      headers,
    });
    return res.data;
  },

  async updateProduct(id, payload) {
    const headers = await getAuthHeaders();
    const res = await axios.put(
      `${API_BASE_URL}/admin/products/${id}`,
      payload,
      { headers },
    );
    return res.data;
  },

  async deleteProduct(product_id) {
    const headers = await getAuthHeaders();
    const res = await axios.delete(
      `${API_BASE_URL}/admin/products/${product_id}`,
      {
        headers,
      },
    );
    return res.data;
  },

  // ===== Orders =====
  async getOrders() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/orders`, { headers });
    return res.data;
  },

  async getOrderById(orderId) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/orders/${orderId}`, {
      headers,
    });
    return res.data;
  },

  async updateOrderStatus(id, data) {
    const headers = await getAuthHeaders();

    // Check if 'data' is a simple string (from the dropdown)
    // or an object (from the manual tracking form)
    const payload = typeof data === "string" ? { status: data } : data;

    const res = await axios.put(
      `${API_BASE_URL}/admin/orders/${id}/status`,
      payload, // ✅ Pass the correctly formatted payload
      { headers },
    );
    return res.data;
  },

  // ===== Inventory (Price + Stock) =====
  async updateInventory(productId, payload) {
    const headers = await getAuthHeaders();
    const res = await axios.put(
      `${API_BASE_URL}/admin/inventory/${productId}`,
      payload,
      { headers },
    );
    return res.data;
  },

  // ===== Customers =====
  async getCustomers() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/customers`, {
      headers,
    });
    return res.data;
  },

  async getCategories() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/categories`, {
      headers,
    });
    return res.data;
  },

  async uploadProductImage(file) {
    const headers = await getAuthHeaders();
    const formData = new FormData();
    formData.append("image", file);

    const res = await axios.post(
      `${API_BASE_URL}/admin/products/upload-image`,
      formData,
      {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    // backend returns: { url: "https://...cloudinary..." }
    return res.data;
  },

  async deleteProductImage(productId, imageUrl) {
    const headers = await getAuthHeaders();
    const res = await axios.delete(
      `${API_BASE_URL}/admin/products/${productId}/images`,
      {
        headers,
        data: { imageUrl }, // axios allows body in DELETE via "data"
      },
    );
    return res.data;
  },

  // ===== Reviews (Admin) =====
  async getAllReviews() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/reviews`, { headers });
    return res.data;
  },

  async deleteReview(productId, reviewId) {
    const headers = await getAuthHeaders();
    const res = await axios.delete(
      `${API_BASE_URL}/admin/reviews/${productId}/${reviewId}`,
      { headers },
    );
    return res.data;
  },

  async updateReview(productId, reviewId, payload) {
    const headers = await getAuthHeaders();
    const res = await axios.put(
      `${API_BASE_URL}/admin/reviews/${productId}/${reviewId}`,
      payload,
      { headers },
    );
    return res.data;
  },

  // ===== Campaigns (Email Marketing) =====
  async sendEmailCampaign(payload) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/campaign/send-email`,
      payload,
      { headers },
    );
    return res.data;
  },

  async getCampaignCustomers() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/campaign/customers`, {
      headers,
    });
    return res.data;
  },

  async generateAWB(orderId, dimensions) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/orders/${orderId}/generate-awb`,
      dimensions,
      { headers },
    );
    return res.data;
  },

  // ===== Billing & Invoices =====
  async getInvoices(params = {}) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/invoices`, {
      headers,
      params,
    });
    return res.data;
  },

  async generateInvoiceRecord(orderId) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/orders/${orderId}/invoice`,
      {},
      { headers },
    );
    return res.data;
  },

  // ===== Manual Billing =====
  async createManualInvoice(payload) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/invoices/manual`,
      payload,
      { headers },
    );
    return res.data;
  },

  // ===== Warranty Registration =====
  async registerWarranty(payload) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/warranty/register`,
      payload,
      { headers },
    );
    return res.data;
  },

  // ─────────────────────────────────────────────────────────────
  // ✅ NEW: Restock Notifications Management
  // ─────────────────────────────────────────────────────────────
  async getAllRestockRequests(status = "") {
    const headers = await getAuthHeaders();
    const query = status && status !== "all" ? `?status=${status}` : "";

    const res = await axios.get(
      `${API_BASE_URL}/admin/restock-requests${query}`,
      { headers },
    );
    return res.data;
  },

  async updateRestockStatus(requestId, newStatus) {
    const headers = await getAuthHeaders();
    const res = await axios.patch(
      `${API_BASE_URL}/admin/restock-requests/${requestId}/status`,
      { status: newStatus },
      { headers },
    );
    return res.data;
  },
};
